import React, { useState } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { Button } from "./ui/button";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * Searchable combobox with "add custom" capability.
 * Props:
 *  - value: current string
 *  - onChange: (val) => void
 *  - options: initial string array
 *  - placeholder
 *  - testid
 */
export default function Combobox({ value, onChange, options = [], placeholder = "Pilih...", testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [custom, setCustom] = useState([]);

  const allOptions = Array.from(new Set([...options, ...custom, ...(value && !options.includes(value) ? [value] : [])]));
  const filtered = query
    ? allOptions.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : allOptions;
  const canAddCustom = query.trim() && !allOptions.some((o) => o.toLowerCase() === query.trim().toLowerCase());

  const handleSelect = (v) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  const handleAddCustom = () => {
    const v = query.trim();
    if (!v) return;
    setCustom((c) => [...c, v]);
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className={cn(
            "h-9 w-full justify-between rounded-sm font-normal text-sm",
            !value && "text-slate-400",
          )}
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-sm" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Cari atau ketik baru..."
            value={query}
            onValueChange={setQuery}
            data-testid={`${testid}-search`}
          />
          <CommandList>
            <CommandEmpty>
              {canAddCustom ? (
                <button
                  type="button"
                  onClick={handleAddCustom}
                  className="w-full flex items-center gap-2 p-2 text-sm hover:bg-slate-50 text-left"
                  data-testid={`${testid}-add-custom`}
                >
                  <Plus className="w-3.5 h-3.5 text-blue-600" />
                  Tambah "<span className="font-semibold">{query}</span>"
                </button>
              ) : (
                <div className="p-2 text-xs text-slate-400 text-center">Tidak ditemukan</div>
              )}
            </CommandEmpty>
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((opt) => (
                  <CommandItem
                    key={opt}
                    value={opt}
                    onSelect={() => handleSelect(opt)}
                    className="text-sm"
                  >
                    <Check className={cn("mr-2 h-3.5 w-3.5", value === opt ? "opacity-100" : "opacity-0")} />
                    {opt}
                  </CommandItem>
                ))}
                {canAddCustom && (
                  <CommandItem
                    value={`__add_${query}`}
                    onSelect={handleAddCustom}
                    className="text-sm text-blue-700"
                    data-testid={`${testid}-add-custom-item`}
                  >
                    <Plus className="mr-2 h-3.5 w-3.5" />
                    Tambah "{query}"
                  </CommandItem>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
